import Plausible from 'plausible-tracker';
import { useEffect } from 'react';
import { clientOnly$ } from 'vite-env-only/macros';

export function setupPlausible(domain?: string) {
    return clientOnly$((() => {
        const plausible = {
            ...Plausible({ domain, trackLocalhost: false, apiHost: location.origin }),
            enableAutoScrollTracking,
            enableExtraEventTracking
        };
        return plausible;
    })());
}

function enableAutoScrollTracking(this: ReturnType<typeof Plausible>) {
    const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                let sectionName: string | undefined = entry.target.id;
                if (!sectionName) {
                    sectionName = entry.target.firstElementChild?.id;
                }
                if (sectionName) {
                    this.trackEvent('Scrolled to ' + sectionName);
                }
            }
        });
    });

    // Assuming you have data-section-name attributes on your sections
    document.querySelectorAll('section').forEach(section => {
        observer.observe(section);
    });

    return () => {
        observer.disconnect();
    };
}

/**
 * Enables tracking of extra events:
 * - Button clicks
 * - Outbound link clicks (only those with target="_blank")
 * - Form submissions
 * - Video play, pause, and complete events
 *
 * Call this function with your plausible tracker object as returned from setupPlausible().
 *
 * @param tracker - The plausible tracker instance.
 */
export function enableExtraEventTracking(this: ReturnType<typeof Plausible>) {
    // Track button clicks
    document.addEventListener('click', event => {
        const target = event.target as HTMLElement;
        if (target?.tagName === 'BUTTON') {
            const text = target.textContent?.trim() || '';
            this.trackEvent(`Button Click ${text}`);
        }

        // Track outbound links (assume all with target="_blank")
        const anchor = target.closest && (target.closest('a') as HTMLAnchorElement | null);
        if (anchor && anchor.target === '_blank' && anchor.href) {
            this.trackEvent(`Outbound Link Click ${anchor.href}`, {
                props: { href: anchor.href, text: anchor.textContent?.trim() || '' }
            });
        }
    });

    // Track form submissions (only user-initiated, not programmatic submits from Conform etc.)
    document.addEventListener('submit', event => {
        if (
            !event.isTrusted || !event.submitter?.parentNode
            || (event.submitter as HTMLButtonElement).name === '__intent__'
        ) {
            return;
        }
        const form = event.target as HTMLFormElement;
        if (form) {
            this.trackEvent('Form Submission', {
                props: { id: form.id || 'unknown', name: form.getAttribute('name') || 'not set' }
            });
        }
    });

    // Track video play, pause, and complete events
    document.querySelectorAll('video').forEach(video => {
        video.addEventListener('play', () => {
            this.trackEvent('Video Play', { props: { id: video.id || 'unknown' } });
        });
        video.addEventListener('pause', () => {
            this.trackEvent('Video Pause', { props: { id: video.id || 'unknown' } });
        });
        video.addEventListener('ended', () => {
            this.trackEvent('Video Complete', { props: { id: video.id || 'unknown' } });
        });
    });
}

export function PlausibleTracker({ domain }: { domain?: string }) {
    useEffect(() => {
        const plausible = setupPlausible(domain);
        if (plausible) {
            plausible.trackPageview();
            plausible.enableAutoPageviews();
            plausible.enableAutoScrollTracking();
            plausible.enableExtraEventTracking();
        }
    }, [ domain ]);
    return null;
}
